import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { METRICS } from "@/lib/data";
import { useI18n } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useLenis";

const HERO_IMG =
  "https://images.pexels.com/photos/30790482/pexels-photo-30790482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900";

export const Hero = () => {
  const { t } = useI18n();
  return (
    <section id="top" className="relative bg-ink text-white overflow-hidden noise pt-[72px]">
      <div className="absolute inset-0 grid-lines opacity-60" />
      {/* top warning strip */}
      <div className="absolute top-[72px] left-0 right-0 h-2 stripes opacity-90" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-8 min-h-[calc(100vh-72px)] items-center py-12 sm:py-16">
        {/* Left: Kinetic headline */}
        <div className="lg:col-span-7 z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center gap-4 mb-8"
          >
            <span className="flex items-center gap-3">
              <span className="h-px w-12 bg-safety" />
              <span className="font-mono2 text-xs tracking-[0.3em] text-safety uppercase">
                {t.hero.eyebrow}
              </span>
            </span>
            <span data-testid="made-in-india-chip" className="inline-flex items-center gap-2 border border-white/20 px-3 py-1 font-mono2 text-[10px] tracking-[0.2em] uppercase text-white/80">
              <span className="flex h-3 w-4 flex-col overflow-hidden rounded-[1px] border border-white/30">
                <span className="flex-1 bg-[#FF9933]" />
                <span className="flex-1 bg-white" />
                <span className="flex-1 bg-[#138808]" />
              </span>
              {t.hero.madeInIndia}
            </span>
          </motion.div>

          <h1 className="font-display font-700 uppercase leading-[0.92] text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight break-words">
            <MaskedLine delay={0.1}>{t.hero.lines[0]}</MaskedLine>
            <MaskedLine delay={0.22} className="text-safety">{t.hero.lines[1]}</MaskedLine>
            <MaskedLine delay={0.34}>{t.hero.lines[2]}</MaskedLine>
            <MaskedLine delay={0.46} className="text-white/40">{t.hero.lines[3]}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="font-deva text-safety text-lg sm:text-xl font-600 mt-5"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-5 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollToId("#quote")}
              data-testid="hero-primary-cta"
              className="group flex items-center justify-center gap-2 px-7 h-14 bg-safety text-white font-mono2 text-sm font-700 uppercase tracking-wider hover:translate-x-[3px] hover:translate-y-[3px] transition-transform hard-shadow"
            >
              {t.cta.requestEstimation}
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
            </button>            <button
              onClick={() => scrollToId("#portfolio")}
              data-testid="hero-secondary-cta"
              className="flex items-center justify-center gap-2 px-7 h-14 border-2 border-white/30 text-white font-mono2 text-sm font-700 uppercase tracking-wider hover:border-safety hover:text-safety transition-colors"
            >
              {t.cta.viewProjects}
            </button>
          </motion.div>
        </div>

        {/* Right: clipped spotlight image */}
        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 1.05, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative border-2 border-white/10"
          >
            <div className="absolute -top-4 -left-4 stripes w-24 h-24 -z-0" />
            <img
              src={HERO_IMG}
              alt="Welder fabricating structural steel with sparks"
              className="relative w-full h-[520px] object-cover grayscale-[0.15] contrast-[1.1]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono2 text-xs text-safety tracking-widest">
              {t.hero.live}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Metric badges */}
      <div className="relative border-t-2 border-white/10 bg-charcoal/60 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.1 }}
              className="py-6 px-5 first:pl-0"
              data-testid={`metric-badge-${i}`}
            >
              <div className="font-display font-700 text-2xl lg:text-3xl text-safety">{m.value}</div>
              <div className="font-mono2 text-[11px] tracking-wider text-white/60 uppercase mt-1">{t.metrics[i]}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-28 right-8 hidden xl:flex flex-col items-center gap-2 text-white/40">
        <span className="font-mono2 text-[10px] tracking-widest [writing-mode:vertical-rl]">{t.hero.scroll}</span>
        <ArrowDown size={16} className="animate-bounce" />
      </div>
    </section>
  );
};
